import { useState, useEffect, useCallback, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { ArrowRight, Calculator, Leaf, Fuel, BatteryCharging, TrendingDown, Info, Sun, Zap, BarChart3, Share2, Copy, CheckCircle2, Link2, Package } from 'lucide-react';
import { trackEvent, EVENTS } from '@/lib/analytics';
import { trackOpenCalculator, trackStartRfq as trackGA4StartRfq } from '@/lib/ga4';
import { SEO } from '@/components/SEO';
import { toast } from 'sonner';
import { fetchCatalogProducts } from '@/lib/catalogApi';

const ASSUMPTIONS = {
  pf: 0.8,
  avgLoad: 0.6,
  sfc: 0.40,
  dieselPrice: 90,
  bessEfficiency: 0.90,
  co2Factor: 2.68,
  solarPerformanceRatio: 0.85,
  solarCostPerKwh: 0,
};

export default function CalculatorPage() {
  const [searchParams] = useSearchParams();
  const [copied, setCopied] = useState(false);
  const [catalogProducts, setCatalogProducts] = useState([]);

  // Read initial values from URL params (for shared links) or use defaults
  const p = (key, fallback) => { const v = searchParams.get(key); return v !== null ? Number(v) : fallback; };
  const [dgRating, setDgRating] = useState(p('dg', 125));
  const [hoursPerDay, setHoursPerDay] = useState(p('hrs', 4));
  const [daysPerMonth, setDaysPerMonth] = useState(p('days', 25));

  // Solar inputs
  const [hasSolar, setHasSolar] = useState(searchParams.get('solar') === '1');
  const [solarKwp, setSolarKwp] = useState(p('skwp', 50));
  const [solarHoursPerDay, setSolarHoursPerDay] = useState(p('shrs', 5));
  const [gridPeakTariff, setGridPeakTariff] = useState(p('peak', 14));
  const [gridOffPeakTariff, setGridOffPeakTariff] = useState(p('offpeak', 7));
  const [peakHoursPerDay, setPeakHoursPerDay] = useState(p('phrs', 4));

  const buildShareUrl = useCallback(() => {
    const params = new URLSearchParams();
    params.set('dg', dgRating);
    params.set('hrs', hoursPerDay);
    params.set('days', daysPerMonth);
    if (hasSolar) {
      params.set('solar', '1');
      params.set('skwp', solarKwp);
      params.set('shrs', solarHoursPerDay);
      params.set('peak', gridPeakTariff);
      params.set('offpeak', gridOffPeakTariff);
      params.set('phrs', peakHoursPerDay);
    }
    return `${window.location.origin}/calculator?${params.toString()}`;
  }, [dgRating, hoursPerDay, daysPerMonth, hasSolar, solarKwp, solarHoursPerDay, gridPeakTariff, gridOffPeakTariff, peakHoursPerDay]);

  const handleCopyLink = async () => {
    const url = buildShareUrl();
    await navigator.clipboard.writeText(url);
    setCopied(true);
    toast.success('Link copied! Anyone with this link will see your exact savings calculation.');
    setTimeout(() => setCopied(false), 2500);
  };

  const handleShare = async () => {
    const url = buildShareUrl();
    if (navigator.share) {
      await navigator.share({
        title: 'Voltide BESS Savings Calculator',
        text: `Check out my BESS savings estimate — ${hasSolar ? 'with solar integration' : 'DG replacement'}`,
        url,
      });
    } else {
      handleCopyLink();
    }
  };

  useEffect(() => {
    trackEvent(EVENTS.OPEN_CALCULATOR);
    trackOpenCalculator(window.location.pathname + window.location.search);
    fetchCatalogProducts().then(({ products }) => setCatalogProducts(products));
  }, []);

  // Find the closest BESS SKU by kWh capacity to the required energy
  const findClosestSku = useCallback((requiredKwh) => {
    if (!catalogProducts.length) return null;
    const eligible = catalogProducts.filter(p => p.rent_from_inr_per_kwh_month && p.battery_kwh > 0);
    if (!eligible.length) return null;
    // Sort by how close kWh is, preferring >= required
    return eligible.reduce((best, p) => {
      const diff = Math.abs(p.battery_kwh - requiredKwh);
      const bestDiff = Math.abs(best.battery_kwh - requiredKwh);
      // Prefer a SKU that covers the need (>= required), with closest match
      if (p.battery_kwh >= requiredKwh && best.battery_kwh < requiredKwh) return p;
      if (best.battery_kwh >= requiredKwh && p.battery_kwh < requiredKwh) return best;
      return diff < bestDiff ? p : best;
    });
  }, [catalogProducts]);

  const calculate = useCallback(() => {
    const loadKw = dgRating * ASSUMPTIONS.pf * ASSUMPTIONS.avgLoad;
    const monthlyHours = hoursPerDay * daysPerMonth;
    const fuelLitres = loadKw * monthlyHours * ASSUMPTIONS.sfc;
    const dieselCost = fuelLitres * ASSUMPTIONS.dieselPrice;

    // --- BESS rental from live catalog (rate × total monthly discharge) ---
    const bessEnergyTotal = loadKw * monthlyHours / ASSUMPTIONS.bessEfficiency;
    const requiredBessKwh = loadKw * hoursPerDay / ASSUMPTIONS.bessEfficiency;
    const matchedSku = findClosestSku(requiredBessKwh);
    let bessRentalCost = 0;
    let matchedProduct = null;
    if (matchedSku && matchedSku.rent_from_inr_per_kwh_month) {
      // per-kWh rate applies to total BESS discharge per month
      bessRentalCost = matchedSku.rent_from_inr_per_kwh_month * bessEnergyTotal;
      matchedProduct = {
        name: matchedSku.product_name,
        slug: matchedSku.slug,
        kWh: matchedSku.battery_kwh,
        ratePerKwh: matchedSku.rent_from_inr_per_kwh_month,
      };
    }

    // --- Solar calculations ---
    let solarDailyGen = 0;
    let solarMonthlyGen = 0;
    let solarToBess = 0;
    let solarSelfConsumption = 0;
    let peakShavingSavings = 0;
    let solarShiftingSavings = 0;
    let totalSolarBenefit = 0;

    if (hasSolar) {
      solarDailyGen = solarKwp * solarHoursPerDay * ASSUMPTIONS.solarPerformanceRatio;
      solarMonthlyGen = solarDailyGen * daysPerMonth;

      const dailyBessNeed = loadKw * hoursPerDay / ASSUMPTIONS.bessEfficiency;

      // Solar energy available for BESS charging (after self-consumption takes ~40% of solar)
      solarSelfConsumption = solarDailyGen * 0.4;
      const solarSurplus = Math.max(0, solarDailyGen - solarSelfConsumption);
      solarToBess = Math.min(solarSurplus, dailyBessNeed);

      // Peak shaving: discharge BESS during peak hours instead of buying at peak tariff
      const peakEnergyShifted = Math.min(loadKw * peakHoursPerDay, dailyBessNeed) * 0.7;
      const dailyPeakSaving = peakEnergyShifted * (gridPeakTariff - gridOffPeakTariff);
      peakShavingSavings = Math.round(dailyPeakSaving * daysPerMonth);

      // Solar shifting: stored solar used in evening avoids grid purchase
      const eveningShift = Math.min(solarToBess * 0.8, loadKw * 3);
      const dailyShiftSaving = eveningShift * gridPeakTariff;
      solarShiftingSavings = Math.round(dailyShiftSaving * daysPerMonth);

      totalSolarBenefit = peakShavingSavings + solarShiftingSavings;
    }

    // BESS cost = rental only (no separate grid charging)
    const effectiveBessCost = hasSolar
      ? Math.max(0, bessRentalCost - totalSolarBenefit)
      : bessRentalCost;

    const dgReplacementSavings = dieselCost - bessRentalCost;
    const totalMonthlySavings = dgReplacementSavings + totalSolarBenefit;
    const annualSavings = totalMonthlySavings * 12;
    const co2Saved = fuelLitres * ASSUMPTIONS.co2Factor;
    const solarCo2Saved = hasSolar ? Math.round(solarMonthlyGen * 0.82) : 0;
    const totalCo2 = co2Saved + solarCo2Saved;
    const savingsPercent = dieselCost > 0 ? (totalMonthlySavings / dieselCost) * 100 : 0;

    return {
      loadKw: loadKw.toFixed(1),
      fuelLitres: Math.round(fuelLitres),
      dieselCost: Math.round(dieselCost),
      bessEnergy: Math.round(bessEnergyTotal),
      bessRentalCost: Math.round(bessRentalCost),
      effectiveBessCost: Math.round(effectiveBessCost),
      dgReplacementSavings: Math.round(dgReplacementSavings),
      matchedProduct,
      requiredBessKwh: Math.round(requiredBessKwh),
      solarDailyGen: Math.round(solarDailyGen),
      solarMonthlyGen: Math.round(solarMonthlyGen),
      solarToBessDaily: Math.round(solarToBess),
      peakShavingSavings,
      solarShiftingSavings,
      totalSolarBenefit,
      totalMonthlySavings: Math.round(totalMonthlySavings),
      annualSavings: Math.round(annualSavings),
      co2Saved: Math.round(totalCo2),
      savingsPercent: savingsPercent.toFixed(0),
    };
  }, [dgRating, hoursPerDay, daysPerMonth, hasSolar, solarKwp, solarHoursPerDay, gridPeakTariff, gridOffPeakTariff, peakHoursPerDay, findClosestSku]);

  const results = calculate();
  const formatINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(n);

  return (
    <div data-testid="calculator-page">
      <SEO
        title="Savings Calculator"
        path="/calculator"
        description="Calculate how much you can save by switching from diesel generators to Voltide battery energy storage, with optional solar integration."
      />
      {/* Hero */}
      <section className="py-16 lg:py-24">
        <div className="voltide-container">
          <div className="max-w-3xl animate-fade-up">
            <Badge className="rounded-full px-4 py-1.5 text-xs font-medium bg-emerald-50 text-emerald-700 border-emerald-100 mb-6">
              Savings Calculator
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-[#1A1A1A] leading-[1.1] mb-6">
              Calculate your savings <span className="blue-gradient-text">when you switch to BESS</span>
            </h1>
            <p className="text-base lg:text-lg text-[#57534E] leading-relaxed max-w-2xl">
              Input your current diesel generator usage and solar setup to see how much you could save with Voltide BESS.
            </p>
          </div>
        </div>
      </section>

      {/* Calculator */}
      <section className="pb-20 lg:pb-24" data-testid="calculator-section">
        <div className="voltide-container">
          <div className="grid lg:grid-cols-5 gap-8">
            {/* Inputs */}
            <div className="lg:col-span-2">
              <div className="bento-card p-6 lg:p-8 sticky top-24">
                <h2 className="text-xl font-bold text-[#1A1A1A] mb-6 flex items-center gap-2">
                  <Fuel className="w-5 h-5 text-[#0061DD]" />
                  Your DG Usage
                </h2>

                <div className="space-y-6">
                  <div data-testid="slider-dg-rating">
                    <div className="flex justify-between mb-3">
                      <label className="text-sm font-medium text-[#1A1A1A]">DG Rating</label>
                      <span className="mono text-sm font-semibold text-[#0061DD]">{dgRating} KVA</span>
                    </div>
                    <Slider value={[dgRating]} onValueChange={([v]) => setDgRating(v)} min={25} max={1000} step={25} />
                    <div className="flex justify-between mt-1 text-xs text-[#A8A29E]"><span>25 KVA</span><span>1000 KVA</span></div>
                  </div>

                  <div data-testid="slider-hours">
                    <div className="flex justify-between mb-3">
                      <label className="text-sm font-medium text-[#1A1A1A]">DG Hours per Day</label>
                      <span className="mono text-sm font-semibold text-[#0061DD]">{hoursPerDay} hrs</span>
                    </div>
                    <Slider value={[hoursPerDay]} onValueChange={([v]) => setHoursPerDay(v)} min={1} max={24} step={1} />
                    <div className="flex justify-between mt-1 text-xs text-[#A8A29E]"><span>1 hr</span><span>24 hrs</span></div>
                  </div>

                  <div data-testid="slider-days">
                    <div className="flex justify-between mb-3">
                      <label className="text-sm font-medium text-[#1A1A1A]">Days per Month</label>
                      <span className="mono text-sm font-semibold text-[#0061DD]">{daysPerMonth} days</span>
                    </div>
                    <Slider value={[daysPerMonth]} onValueChange={([v]) => setDaysPerMonth(v)} min={1} max={30} step={1} />
                    <div className="flex justify-between mt-1 text-xs text-[#A8A29E]"><span>1 day</span><span>30 days</span></div>
                  </div>
                </div>

                {/* Solar Section */}
                <div className="mt-8 pt-6 border-t border-stone-200">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2">
                      <Sun className="w-5 h-5 text-amber-500" />
                      <h2 className="text-lg font-bold text-[#1A1A1A]">Solar Installation</h2>
                    </div>
                    <Switch checked={hasSolar} onCheckedChange={setHasSolar} data-testid="solar-toggle" />
                  </div>

                  {hasSolar && (
                    <div className="space-y-6 animate-fade-up" data-testid="solar-inputs">
                      <div data-testid="slider-solar-kwp">
                        <div className="flex justify-between mb-3">
                          <label className="text-sm font-medium text-[#1A1A1A]">Solar Capacity</label>
                          <span className="mono text-sm font-semibold text-amber-600">{solarKwp} kWp</span>
                        </div>
                        <Slider value={[solarKwp]} onValueChange={([v]) => setSolarKwp(v)} min={5} max={500} step={5} />
                        <div className="flex justify-between mt-1 text-xs text-[#A8A29E]"><span>5 kWp</span><span>500 kWp</span></div>
                      </div>

                      <div data-testid="slider-solar-hours">
                        <div className="flex justify-between mb-3">
                          <label className="text-sm font-medium text-[#1A1A1A]">Avg. Solar Hours/Day</label>
                          <span className="mono text-sm font-semibold text-amber-600">{solarHoursPerDay} hrs</span>
                        </div>
                        <Slider value={[solarHoursPerDay]} onValueChange={([v]) => setSolarHoursPerDay(v)} min={3} max={8} step={0.5} />
                        <div className="flex justify-between mt-1 text-xs text-[#A8A29E]"><span>3 hrs</span><span>8 hrs</span></div>
                      </div>

                      <div className="p-4 rounded-xl bg-amber-50/50 border border-amber-100">
                        <p className="text-xs font-semibold text-amber-800 mb-2">Grid Tariff Settings</p>
                        <div className="space-y-4">
                          <div data-testid="slider-peak-tariff">
                            <div className="flex justify-between mb-2">
                              <label className="text-xs text-[#57534E]">Peak Tariff</label>
                              <span className="mono text-xs font-semibold text-amber-700">₹{gridPeakTariff}/kWh</span>
                            </div>
                            <Slider value={[gridPeakTariff]} onValueChange={([v]) => setGridPeakTariff(v)} min={8} max={25} step={0.5} />
                          </div>
                          <div data-testid="slider-offpeak-tariff">
                            <div className="flex justify-between mb-2">
                              <label className="text-xs text-[#57534E]">Off-Peak Tariff</label>
                              <span className="mono text-xs font-semibold text-amber-700">₹{gridOffPeakTariff}/kWh</span>
                            </div>
                            <Slider value={[gridOffPeakTariff]} onValueChange={([v]) => setGridOffPeakTariff(v)} min={3} max={15} step={0.5} />
                          </div>
                          <div data-testid="slider-peak-hours">
                            <div className="flex justify-between mb-2">
                              <label className="text-xs text-[#57534E]">Peak Hours/Day</label>
                              <span className="mono text-xs font-semibold text-amber-700">{peakHoursPerDay} hrs</span>
                            </div>
                            <Slider value={[peakHoursPerDay]} onValueChange={([v]) => setPeakHoursPerDay(v)} min={2} max={8} step={1} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6 p-4 rounded-xl bg-stone-50 border border-stone-200">
                  <div className="flex items-start gap-2">
                    <Info className="w-4 h-4 text-[#A8A29E] mt-0.5 shrink-0" />
                    <p className="text-xs text-[#A8A29E] leading-relaxed">
                      Assumptions: PF {ASSUMPTIONS.pf}, Avg. load {ASSUMPTIONS.avgLoad * 100}%, SFC {ASSUMPTIONS.sfc} L/kWh, Diesel {formatINR(ASSUMPTIONS.dieselPrice)}/L, BESS efficiency {ASSUMPTIONS.bessEfficiency * 100}%
                      {hasSolar && `, Solar PR ${ASSUMPTIONS.solarPerformanceRatio * 100}%, Solar self-consumption 40%`}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Results */}
            <div className="lg:col-span-3">
              <div className="space-y-6">
                <h2 className="text-xl font-bold text-[#1A1A1A] flex items-center gap-2">
                  <TrendingDown className="w-5 h-5 text-emerald-500" />
                  Your Potential Savings
                </h2>

                {/* Big numbers */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="bento-card p-6 bg-emerald-50 border-emerald-100" data-testid="result-monthly-savings">
                    <p className="text-sm font-medium text-emerald-700 mb-1">Monthly Savings</p>
                    <p className="mono text-3xl sm:text-4xl font-bold text-emerald-700">{formatINR(results.totalMonthlySavings)}</p>
                    <p className="text-sm text-emerald-600 mt-1">{results.savingsPercent}% lower than diesel{hasSolar ? ' + grid peak' : ''}</p>
                  </div>
                  <div className="bento-card p-6 bg-blue-50 border-blue-100" data-testid="result-annual-savings">
                    <p className="text-sm font-medium text-[#0061DD] mb-1">Annual Savings</p>
                    <p className="mono text-3xl sm:text-4xl font-bold text-[#0061DD]">{formatINR(results.annualSavings)}</p>
                    <p className="text-sm text-blue-600 mt-1">Projected yearly benefit</p>
                  </div>
                </div>

                {/* Solar benefit breakdown */}
                {hasSolar && results.totalSolarBenefit > 0 && (
                  <div className="bento-card p-6 bg-amber-50/50 border border-amber-100" data-testid="solar-benefit-breakdown">
                    <h3 className="text-lg font-bold text-[#1A1A1A] mb-4 flex items-center gap-2">
                      <Sun className="w-5 h-5 text-amber-500" />
                      Solar + Storage Benefits
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <BarChart3 className="w-4 h-4 text-amber-600" />
                          <span className="text-sm text-[#57534E]">Peak Shaving</span>
                        </div>
                        <div className="text-right">
                          <span className="mono text-sm font-semibold text-emerald-600">{formatINR(results.peakShavingSavings)}/mo</span>
                          <p className="text-[10px] text-[#A8A29E]">Avoid ₹{gridPeakTariff}/kWh during peak hours</p>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <Zap className="w-4 h-4 text-amber-600" />
                          <span className="text-sm text-[#57534E]">Solar Shifting</span>
                        </div>
                        <div className="text-right">
                          <span className="mono text-sm font-semibold text-emerald-600">{formatINR(results.solarShiftingSavings)}/mo</span>
                          <p className="text-[10px] text-[#A8A29E]">Stored solar used after sunset</p>
                        </div>
                      </div>
                      <div className="border-t border-amber-200 pt-3 flex justify-between items-center">
                        <span className="text-sm font-semibold text-[#1A1A1A]">Total Solar Benefit</span>
                        <span className="mono text-base font-bold text-emerald-700">{formatINR(results.totalSolarBenefit)}/mo</span>
                      </div>
                    </div>
                    <div className="mt-3 p-3 rounded-lg bg-white/60">
                      <p className="text-xs text-[#57534E]">
                        Your {solarKwp} kWp system generates ~{results.solarDailyGen} kWh/day.
                        About {results.solarToBessDaily} kWh/day charges the BESS for free.
                      </p>
                    </div>
                  </div>
                )}

                {/* CO2 & Details */}
                <div className="grid sm:grid-cols-3 gap-4">
                  <div className="bento-card p-5" data-testid="result-co2">
                    <div className="flex items-center gap-2 mb-2">
                      <Leaf className="w-5 h-5 text-emerald-500" />
                      <p className="text-sm font-medium text-[#57534E]">CO2 Saved/month</p>
                    </div>
                    <p className="mono text-2xl font-bold text-[#1A1A1A]">{results.co2Saved.toLocaleString()} kg</p>
                  </div>
                  <div className="bento-card p-5" data-testid="result-fuel">
                    <div className="flex items-center gap-2 mb-2">
                      <Fuel className="w-5 h-5 text-amber-500" />
                      <p className="text-sm font-medium text-[#57534E]">Diesel Eliminated</p>
                    </div>
                    <p className="mono text-2xl font-bold text-[#1A1A1A]">{results.fuelLitres.toLocaleString()} L/mo</p>
                  </div>
                  <div className="bento-card p-5" data-testid="result-bess-energy">
                    <div className="flex items-center gap-2 mb-2">
                      <BatteryCharging className="w-5 h-5 text-[#0061DD]" />
                      <p className="text-sm font-medium text-[#57534E]">BESS Energy</p>
                    </div>
                    <p className="mono text-2xl font-bold text-[#1A1A1A]">{results.bessEnergy.toLocaleString()} kWh</p>
                  </div>
                </div>

                {/* Recommended BESS Configuration */}
                {results.matchedProduct && (
                  <div className="bento-card p-5 border-blue-100 bg-blue-50/30" data-testid="recommended-bess">
                    <div className="flex items-start gap-3">
                      <Package className="w-6 h-6 text-[#0061DD] shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <p className="text-sm font-semibold text-[#1A1A1A] mb-1">Recommended BESS Configuration</p>
                        <p className="text-xs text-[#57534E] leading-relaxed mb-2">
                          Based on your {results.requiredBessKwh} kWh daily requirement
                        </p>
                        <div className="flex flex-wrap items-center gap-3">
                          <Link to={`/products/${results.matchedProduct.slug}`} className="text-sm font-medium text-[#0061DD] hover:underline">
                            {results.matchedProduct.name} ({results.matchedProduct.kWh} kWh)
                          </Link>
                          {results.matchedProduct.units > 1 && (
                            <Badge className="rounded-full text-[10px] bg-blue-100 text-[#0061DD] border-0">
                              x{results.matchedProduct.units} units
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Cost Breakdown */}
                <div className="bento-card p-6" data-testid="cost-breakdown">
                  <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Cost Breakdown</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-[#57534E]">Current Diesel Cost/month</span>
                      <span className="mono text-sm font-semibold text-red-600">{formatINR(results.dieselCost)}</span>
                    </div>
                    <div className="border-t border-stone-100 pt-3">
                      <p className="text-xs font-semibold text-[#A8A29E] uppercase tracking-wider mb-2">BESS Alternative</p>
                    </div>
                    {results.bessRentalCost > 0 && (
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-sm text-[#57534E]">BESS Rental/month</span>
                          {results.matchedProduct && (
                            <p className="text-[10px] text-[#A8A29E]">₹{Number(results.matchedProduct.ratePerKwh).toFixed(2)}/kWh × {results.bessEnergy.toLocaleString()} kWh discharged</p>
                          )}
                        </div>
                        <span className="mono text-sm font-semibold text-[#0061DD]">{formatINR(results.bessRentalCost)}</span>
                      </div>
                    )}
                    <div className="border-t border-stone-200 pt-3 flex justify-between items-center">
                      <span className="text-sm font-medium text-[#1A1A1A]">DG Replacement Savings</span>
                      <span className="mono text-sm font-bold text-emerald-600">{formatINR(results.dgReplacementSavings)}</span>
                    </div>
                    {hasSolar && results.totalSolarBenefit > 0 && (
                      <>
                        <div className="flex justify-between items-center">
                          <span className="text-sm text-amber-700 flex items-center gap-1"><Sun className="w-3.5 h-3.5" /> Solar + Storage Benefits</span>
                          <span className="mono text-sm font-bold text-emerald-600">+{formatINR(results.totalSolarBenefit)}</span>
                        </div>
                        <div className="border-t border-stone-200 pt-3 flex justify-between items-center">
                          <span className="text-sm font-semibold text-[#1A1A1A]">Total Net Savings/month</span>
                          <span className="mono text-lg font-bold text-emerald-600">{formatINR(results.totalMonthlySavings)}</span>
                        </div>
                      </>
                    )}
                    {!hasSolar && (
                      <div className="border-t border-stone-200 pt-3 flex justify-between items-center">
                        <span className="text-sm font-semibold text-[#1A1A1A]">Net Savings/month</span>
                        <span className="mono text-lg font-bold text-emerald-600">{formatINR(results.totalMonthlySavings)}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Visual bar comparison */}
                <div className="bento-card p-6" data-testid="visual-comparison">
                  <h3 className="text-lg font-bold text-[#1A1A1A] mb-4">Visual Comparison</h3>
                  <div className="space-y-4">
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-[#57534E]">Diesel Cost</span>
                        <span className="mono font-medium text-red-600">{formatINR(results.dieselCost)}</span>
                      </div>
                      <div className="h-4 bg-stone-100 rounded-full overflow-hidden">
                        <div className="h-full bg-red-400 rounded-full transition-all duration-500" style={{ width: '100%' }} />
                      </div>
                    </div>
                    <div>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-[#57534E]">BESS Rental</span>
                        <span className="mono font-medium text-[#0061DD]">{formatINR(results.bessRentalCost)}</span>
                      </div>
                      <div className="h-4 bg-stone-100 rounded-full overflow-hidden">
                        <div className="h-full bg-[#0061DD] rounded-full transition-all duration-500" style={{ width: `${results.dieselCost > 0 ? (results.bessRentalCost / results.dieselCost * 100) : 0}%` }} />
                      </div>
                    </div>
                    {hasSolar && (
                      <div>
                        <div className="flex justify-between text-sm mb-1">
                          <span className="text-[#57534E]">BESS + Solar (effective cost)</span>
                          <span className="mono font-medium text-emerald-600">{formatINR(results.effectiveBessCost)}</span>
                        </div>
                        <div className="h-4 bg-stone-100 rounded-full overflow-hidden">
                          <div className="h-full bg-emerald-400 rounded-full transition-all duration-500" style={{ width: `${results.dieselCost > 0 ? (results.effectiveBessCost / results.dieselCost * 100) : 0}%` }} />
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Solar upsell if not enabled */}
                {!hasSolar && (
                  <div className="bento-card p-5 border-amber-100 bg-amber-50/30" data-testid="solar-upsell">
                    <div className="flex items-start gap-3">
                      <Sun className="w-6 h-6 text-amber-500 shrink-0 mt-0.5" />
                      <div>
                        <p className="text-sm font-semibold text-[#1A1A1A] mb-1">Have a solar installation?</p>
                        <p className="text-xs text-[#57534E] leading-relaxed mb-3">
                          Enable the solar option above to see additional savings from peak shaving, solar shifting, and free BESS charging from your panels.
                        </p>
                        <Button variant="outline" className="rounded-full text-xs border-amber-200 text-amber-700 hover:bg-amber-50 gap-1" onClick={() => setHasSolar(true)} data-testid="enable-solar-btn">
                          <Sun className="w-3.5 h-3.5" /> Enable Solar Savings
                        </Button>
                      </div>
                    </div>
                  </div>
                )}

                {/* Share Results */}
                <div className="bento-card p-5" data-testid="share-results-section">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-sm font-semibold text-[#1A1A1A] mb-0.5">Share these results</h3>
                      <p className="text-xs text-[#57534E]">Send your savings analysis to colleagues for review</p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        className={`rounded-full text-xs gap-1.5 transition-all ${copied ? 'border-emerald-300 text-emerald-700 bg-emerald-50' : ''}`}
                        onClick={handleCopyLink}
                        data-testid="calc-copy-link-btn"
                      >
                        {copied ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Link2 className="w-3.5 h-3.5" />}
                        {copied ? 'Copied!' : 'Copy Link'}
                      </Button>
                      <Button
                        variant="outline"
                        className="rounded-full text-xs gap-1.5"
                        onClick={handleShare}
                        data-testid="calc-share-btn"
                      >
                        <Share2 className="w-3.5 h-3.5" /> Share
                      </Button>
                    </div>
                  </div>
                </div>

                {/* CTA */}
                <div className="flex flex-wrap gap-4 pt-4">
                  <Link to={`/quote?use_case=${hasSolar ? 'solar_shifting' : 'dg_replacement'}`} onClick={() => { trackEvent(EVENTS.START_RFQ, { source: 'calculator', dgRating, hasSolar, savings: results.annualSavings }); trackGA4StartRfq('calculator', 1); }}>
                    <Button className="rounded-full bg-[#0061DD] hover:bg-[#004BB3] text-white px-8 py-6 text-lg font-semibold gap-2" data-testid="calc-get-quote-btn">
                      Get a Custom Quote <ArrowRight className="w-5 h-5" />
                    </Button>
                  </Link>
                  <Link to="/products">
                    <Button variant="outline" className="rounded-full border-stone-200 text-[#57534E] hover:text-[#0061DD] hover:border-[#0061DD] px-8 py-6 text-lg font-medium">
                      Browse Products
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
